/**
 * PartnershipWarrantyRegistrations - live list of warranties the
 * contractor has registered, grouped by warranty category
 * (Platinum / Preferred / Standard / …). Each category row shows a
 * count and expands to the individual registrations beneath it, with
 * a link into each holding's detail page. HQ partnerships roll up
 * sub-location registrations (see the backend service).
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Loader2, ChevronRight } from 'lucide-react'

import {
  usePartnershipWarrantyRegistrations,
  type WarrantyRegistrationRow,
} from '@/admin/hooks/use-partnership-warranty-registrations'
import { useFormatting } from '@/admin/hooks/use-formatting'

interface Props {
  partnershipUuid: string
}

const TYPE_LABEL: Record<string, string> = {
  MANUFACTURER_WARRANTY: 'Standard',
  EXTENDED_WARRANTY: 'Extended',
  RETAILER_WARRANTY: 'Retailer',
}

function humaniseType(warrantyType: string | null | undefined): string {
  if (!warrantyType) return ''
  return TYPE_LABEL[warrantyType] ?? warrantyType
}

export function PartnershipWarrantyRegistrations({ partnershipUuid }: Props) {
  const { data, isLoading } = usePartnershipWarrantyRegistrations(partnershipUuid)
  const navigate = useNavigate()
  const { formatDate: fmtDate } = useFormatting()
  const formatDate = (iso: string | null): string => (iso ? fmtDate(iso) : '-')

  const rows = useMemo(() => data ?? [], [data])

  // Bucket by warranty_category_name. Within each class, keep the
  // server ordering (recent first) so drill-downs lead with the
  // newest registration.
  const groupedByClass = useMemo(() => {
    const buckets = new Map<string, WarrantyRegistrationRow[]>()
    for (const r of rows) {
      const key = r.warranty_category_name || 'Uncategorised'
      const existing = buckets.get(key) ?? []
      existing.push(r)
      buckets.set(key, existing)
    }
    // Sort classes by count desc, then alphabetically - the heaviest
    // category bubbles to the top.
    return Array.from(buckets.entries()).sort(
      ([aKey, aRows], [bKey, bRows]) => {
        if (aRows.length !== bRows.length) return bRows.length - aRows.length
        return aKey.localeCompare(bKey)
      },
    )
  }, [rows])

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="clara-section-label">Registered Warranties</h3>
          {rows.length > 0 && (
            <span className="rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[9px] font-normal uppercase tracking-[0.1em] text-[var(--green)]">
              {rows.length}
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : rows.length === 0 ? (
        <div className="max-w-[480px] rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-5">
          <p className="font-display text-[15px] text-[var(--ink-3)]">
            No warranties have been registered through this partner yet.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {groupedByClass.map(([className, classRows], idx) => {
            const isExpanded = expanded.has(className)
            const types = Array.from(
              new Set(classRows.map((r) => r.warranty_type)),
            ).filter((v): v is string => typeof v === 'string' && v.length > 0)
            return (
              <div
                key={className}
                className={idx > 0 ? 'border-t-[0.5px] border-t-[var(--rule)]' : ''}
              >
                <button
                  type="button"
                  onClick={() => toggle(className)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-[var(--bg-subtle)] cursor-pointer"
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 shrink-0 text-[var(--ink-3)] transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                  />
                  <span className="text-[13px] text-[var(--ink)]">{className}</span>
                  <span className="text-[11px] text-[var(--ink-3)]">
                    {classRows.length}{' '}
                    {classRows.length === 1 ? 'registration' : 'registrations'}
                  </span>
                  <div className="ml-auto flex items-center gap-1.5">
                    {types.map((typ) => (
                      <span
                        key={typ}
                        className="rounded-[3px] bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[9px] font-[500] uppercase tracking-[0.1em] text-[var(--ink-3)]"
                      >
                        {humaniseType(typ)}
                      </span>
                    ))}
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t-[0.5px] border-t-[var(--rule-light)] bg-[var(--bg-subtle)]">
                    {classRows.map((r) => {
                      const consumer =
                        r.consumer_name || r.consumer_email || '-'
                      return (
                        <button
                          key={r.warranty_uuid}
                          type="button"
                          onClick={() =>
                            navigate(`/registrations/${r.holding_uuid}`)
                          }
                          className="flex w-full items-center gap-3 border-b-[0.5px] border-b-[var(--rule-light)] px-10 py-2.5 text-left transition-colors duration-150 last:border-b-0 hover:bg-[var(--bg)] cursor-pointer"
                        >
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-[12px] text-[var(--ink)]">
                              {r.product_name || r.template_name}
                            </span>
                            <span className="truncate text-[10px] text-[var(--ink-3)]">
                              {[consumer, r.registration_number, r.location_name]
                                .filter(
                                  (v): v is string =>
                                    typeof v === 'string' && v.length > 0,
                                )
                                .join(' · ')}
                            </span>
                          </div>
                          <span className="shrink-0 text-[10px] text-[var(--ink-3)]">
                            {formatDate(r.active_from)}
                          </span>
                          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
